import { type EditorView } from 'prosemirror-view'
import { uniq } from 'lodash'
import { useMemo } from 'react'

import { CellSelection } from 'common/Prose/utils/table/CellSelection'
import { DEFAULT_BACKGROUND_CELL_COLOR, DEFAULT_BORDER_COLOR } from '../constants/colorOptions'
import { TableMap } from 'common/Prose/utils/table/TableMap'
import { isTableSelection } from 'common/Prose/utils/table'
import { BorderSelection } from '../utils/table/BorderSelection'
import { borderSelectionsToNodes } from '../utils/table/borderSelectionsToNodes'
import { parseBorderAttr } from '../utils/table/borderAttr'

export function useActiveCellColorsInSelection(view: EditorView) {
  return useMemo(() => {
    const selection = view.state.selection
    const colors: string[] = []
    if (selection instanceof CellSelection || isTableSelection(selection)) {
      let $cells
      if (selection instanceof CellSelection) $cells = selection.$includedCells
      else {
        const tableMap = new TableMap({ posBeforeTable: selection.from, node: selection.node })

        $cells = tableMap.getPosBeforeCellList().map((pos) => view.state.doc.resolve(pos))
      }

      $cells.forEach(($cell) => {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const color = $cell.nodeAfter!.attrs.background
        const rowColor = $cell.node().attrs.background
        const tableColor = $cell.node(-1).attrs.background

        if (color) colors.push(color)
        if (rowColor && !color) colors.push(rowColor)
        if (tableColor && !rowColor && !color) colors.push(tableColor)
      })
    }

    return colors.length ? uniq(colors) : [DEFAULT_BACKGROUND_CELL_COLOR]
  }, [view.state.doc, view.state.selection])
}

export function useActiveBorderColorsInSelection(view: EditorView) {
  return useMemo(() => {
    const state = view.state
    const { selection } = state
    if (
      !(selection instanceof BorderSelection) &&
      !(selection instanceof CellSelection) &&
      !isTableSelection(selection)
    ) {
      return [DEFAULT_BORDER_COLOR]
    }

    const colors: string[] = []
    if (selection instanceof BorderSelection) {
      const tableNode = selection.$tablePos.nodeAfter
      if (tableNode != null && tableNode.type === view.state.schema.nodes.table) {
        const borderInfo = borderSelectionsToNodes(tableNode, selection, view.state.doc)
        for (const { direction, node } of borderInfo) {
          colors.push(parseBorderAttr(node.attrs[direction]).color)
        }
      }
    } else if (selection instanceof CellSelection) {
      // TODO: Implement this after adding some helpers for getting all BorderSelections or positions from CellSelections.
      // See setBorderAttr().
    } /* table selection */ else {
      // TODO: Implement this after adding some helpers for getting all BorderSelections or positions of a table.
    }

    return colors.length ? uniq(colors) : [DEFAULT_BORDER_COLOR]
  }, [view.state])
}
